// react
import { ReactNode, useContext } from 'react';

// types
import {
	Contract,
	Document,
	DocumentType,
	Entity,
	EntityType,
	Frame,
	Transaction,
	User,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';

// icons
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { ModeEditOutlined as IconModeEdit } from '@mui/icons-material';

// ui
import { BlockchainHash } from '../../ui/BlockchainHash/BlockchainHash';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// dialogs
import { DialogEditDocument } from '../../dialogs/DialogEditDocument/DialogEditDocument';

// links
import { LinkContract } from '../../links/LinkContract/LinkContract';
import { LinkDocument } from '../../links/LinkDocument/LinkDocument';
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';
import { LinkTransaction } from '../../links/LinkTransaction/LinkTransaction';

// theme
import { theme } from '../../../theme';

// utils
import { translateEntityType } from '../../../utils/translations';

// props
export interface CardHistoricalTransactionProps {
	document: Document;
	entityType?: EntityType;
	frame?: Frame;
	refetchDocuments?: () => void;
}

export const CardDocument = ({
	document,
	entityType,
	frame = 'PROJECT',
	refetchDocuments,
}: CardHistoricalTransactionProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogDocument = searchParams.get('dialogDocument');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// vars
	const contract = document.contract as Contract;
	const docTransaction = document.transaction as Transaction;
	const docType = document?.type as unknown as DocumentType;
	const docUser = document?.user as User;

	// handlers
	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogDocument');
		setSearchParams(searchParams);
	};

	// vars
	const btnMenuItems: MenuItemProps[] = [];
	const hasColumnContract = entityType !== 'BENEFICIARY';
	const hasColumnEntity = ['BENEFICIARIES', 'SUPPLIERS'].includes(frame) && entityType;

	if (userProjectPermissions?.document?.update) {
		btnMenuItems.push({
			children: t('editDocument'),
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editDocument');
				searchParams.set('dialogDocument', String(document.id));
				setSearchParams(searchParams);
			},
		});
	}

	let valueDocumentEntities: ReactNode = t('na');

	if (document.entities.length) {
		let documentEntities = document.entities as Entity[];
		let entity = documentEntities[0];

		if (frame === 'BENEFICIARIES') {
			documentEntities = documentEntities.filter((entity) => entity.type === 'BENEFICIARY');
			const entityBeneficiary = documentEntities.find((entity) => entity.type === 'BENEFICIARY');

			if (entityBeneficiary) {
				entity = entityBeneficiary;
			}
		} else if (frame === 'SUPPLIERS') {
			documentEntities = documentEntities.filter((entity) => entity.type === 'SUPPLIER');
			const entitySupplier = documentEntities.find((entity) => entity.type === 'SUPPLIER');

			if (entitySupplier) {
				entity = entitySupplier;
			}
		}

		valueDocumentEntities = (
			<Grid container justifyContent={{ xs: 'flex-end', lg: 'flex-start' }} spacing={1}>
				<Grid item>
					<Grid alignItems="center" container spacing={1}>
						<Grid flex={1} item xs={true}>
							<LinkEntity entity={entity} />
						</Grid>
						{documentEntities.length > 1 && (
							<Grid item xs="auto">
								+{documentEntities.length - 1}
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
		);
	}

	return (
		<>
			<Paper sx={{ position: 'relative' }}>
				<Box sx={{ padding: theme.spacing(2) }}>
					<Grid container alignItems="center" spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={2}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle type={'document'} />
									</Grid>
									<Grid item>
										<TypographyCaption id={document.id} />
										<LinkDocument document={document} underline="hover" />
									</Grid>
								</Grid>
							</Box>
						</Grid>

						<Grid item xs={12} lg={frame === 'BENEFICIARY' ? 3 : 1.5}>
							<RowKeyValue
								label={t('type')}
								value={<Typography>{docType?.name || t('na')}</Typography>}
							/>
						</Grid>

						{hasColumnEntity && (
							<Grid item xs={12} lg={2}>
								<RowKeyValue
									label={translateEntityType(entityType, t)}
									value={valueDocumentEntities}
								/>
							</Grid>
						)}

						{hasColumnContract && (
							<Grid item xs={12} lg={1.5}>
								<RowKeyValue
									label={t('contract')}
									value={
										<Typography>
											<LinkContract contract={contract as Contract} />
										</Typography>
									}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={hasColumnEntity ? 1.5 : 2}>
							<RowKeyValue
								label={t('transaction')}
								value={
									<LinkTransaction
										GridProps={{ width: 'auto', justifySelf: { xs: 'flex-end', lg: 'flex-start' } }}
										transaction={docTransaction}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('created')}
								value={<UserAvatarModified date={document.created} user={docUser} />}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('hash')}
								value={
									document.notarizationHash ? (
										<BlockchainHash hash={document.notarizationHash} />
									) : (
										<Typography>{t('na')}</Typography>
									)
								}
							/>
						</Grid>
						{btnMenuItems.length > 0 && (
							<Grid item xs={12} lg={true}>
								<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Box>
			</Paper>
			<DialogEditDocument
				isOpen={paramDialog === 'editDocument' && paramDialogDocument === String(document.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchDocuments) {
						refetchDocuments();
					}
				}}
			/>
		</>
	);
};
